import { SECURITY_CONFIG, SecurityEventType, logSecurityEvent } from './security';

/**
 * Sanitizes text input to prevent XSS attacks and prompt injection
 * @param input - The text to sanitize
 * @returns Sanitized text safe for display and AI processing
 */
export const sanitizeText = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // First apply prompt injection protection
  const promptSafe = sanitizePromptInjection(input);
  
  // Then apply XSS protection
  return promptSafe
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

/**
 * Detects and filters prompt injection attempts
 * @param input - The text to check for prompt injection
 * @returns Sanitized text with prompt injection patterns removed
 */
export const sanitizePromptInjection = (input: string): string => {
  if (!input || typeof input !== 'string') {
    return '';
  }

  // Limit input length to prevent overwhelming prompts
  if (input.length > SECURITY_CONFIG.MAX_INPUT_LENGTH) {
    input = input.substring(0, SECURITY_CONFIG.MAX_INPUT_LENGTH);
  }

  let sanitized = input;
  let hasBlocked = false;
  
  // Enhanced pattern detection for prompt injection
  const additionalPatterns = [
    // Advanced role manipulation
    /\b(become|transform into|switch to|change to)\s+(system|assistant|admin)/gi,
    /\b(now you are|from now on|starting now)\s+.*(system|assistant|admin)/gi,
    
    // Context escape attempts
    /\\n\\n|\\r\\n|\\t/gi, // Encoded newlines/tabs
    /\u000a|\u000d|\u0009/gi, // Unicode control chars
    
    // Instruction injection
    /\b(execute|run|perform)\s+(command|instruction|task)/gi,
    /\b(output|return|display)\s+.*(password|token|key|secret)/gi,
    
    // Multi-language injection attempts
    /\b(ejecutar|ausführen|実行|执行)\b/gi, // Execute in other languages
    
    // Encoding bypass attempts
    /%[0-9a-f]{2}/gi, // URL encoding
    /&#x?[0-9a-f]+;/gi, // HTML entities
  ];
  
  // Apply centralized patterns
  SECURITY_CONFIG.BLOCKED_PATTERNS.forEach(pattern => {
    if (pattern.test(sanitized)) {
      hasBlocked = true;
      sanitized = sanitized.replace(pattern, '[FILTERED]');
    }
  });
  
  // Apply additional enhanced patterns
  additionalPatterns.forEach(pattern => {
    if (pattern.test(sanitized)) {
      hasBlocked = true;
      sanitized = sanitized.replace(pattern, '[FILTERED]');
    }
  });

  if (hasBlocked) {
    logSecurityEvent(SecurityEventType.PROMPT_INJECTION_BLOCKED, { originalLength: input.length });
  }

  // Remove excessive special characters that might be used for injection
  sanitized = sanitized.replace(/[{}\[\]<>]{3,}/g, '');
  
  // Remove multiple consecutive newlines/spaces that might be used for formatting attacks
  sanitized = sanitized.replace(/\n{3,}/g, '\n\n');
  sanitized = sanitized.replace(/\s{5,}/g, ' ');
  
  // Remove potential encoding bypasses
  sanitized = sanitized.replace(/\\[nrt]/g, ' ');
  
  return sanitized.trim();
};

/**
 * Validates and sanitizes speech recognition input
 * @param transcript - The speech recognition result
 * @returns Clean, safe transcript
 */
export const sanitizeSpeechInput = (transcript: string): string => {
  if (!transcript) return '';
  
  // Remove any potential script tags or suspicious patterns
  const cleaned = transcript
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/data:/gi, '');
  
  return sanitizeText(cleaned);
};

/**
 * Validates AI response content for safety
 * @param response - The AI response to validate
 * @returns Sanitized response safe for display
 */
export const validateAIResponse = (response: string): string => {
  if (!response || typeof response !== 'string') {
    return '';
  }

  let sanitized = response;
  let hasFiltered = false;

  // Apply response filters from security config
  SECURITY_CONFIG.RESPONSE_FILTERS.forEach(pattern => {
    if (pattern.test(sanitized)) {
      hasFiltered = true;
      sanitized = sanitized.replace(pattern, '[Content Filtered]');
    }
  });

  if (hasFiltered) {
    logSecurityEvent(SecurityEventType.RESPONSE_FILTERED, { originalLength: response.length });
  }

  // Limit response length
  if (sanitized.length > SECURITY_CONFIG.MAX_RESPONSE_LENGTH) {
    sanitized = sanitized.substring(0, SECURITY_CONFIG.MAX_RESPONSE_LENGTH) + '...';
  }

  return sanitized.trim();
};

/**
 * Validates input before sending to AI API
 * @param input - User input to validate
 * @returns Object with validation result and sanitized input
 */
export const validateUserInput = (input: string): { isValid: boolean; sanitized: string; reason?: string } => {
  if (!input || typeof input !== 'string') {
    return { isValid: false, sanitized: '', reason: SECURITY_CONFIG.ERROR_MESSAGES.INVALID_INPUT };
  }

  // Check for excessive length
  if (input.length > SECURITY_CONFIG.MAX_INPUT_LENGTH) {
    logSecurityEvent(SecurityEventType.INVALID_INPUT_BLOCKED, { reason: 'input_too_long', length: input.length });
    return { isValid: false, sanitized: '', reason: SECURITY_CONFIG.ERROR_MESSAGES.INPUT_TOO_LONG };
  }

  // Enhanced suspicious pattern detection
  const suspiciousPatterns = [
    // Long system instructions
    /\b(system:|assistant:|user:)\s*[\s\S]{50,}/gi,
    // Bracketed role instructions
    /\[\s*(system|assistant|user)\s*\][\s\S]{50,}/gi,
    // Instruction override attempts
    /(ignore|disregard|override)\s+(all|previous|above)\s+instructions?/gi,
    // Complex injection patterns
    /\b(pretend|imagine|roleplay)\s+.*(system|admin|root)/gi,
    // Encoding attempts
    /(%[0-9a-f]{2}){5,}/gi, // Multiple URL encoded chars
    /(&#x?[0-9a-f]+;){3,}/gi, // Multiple HTML entities
    // Repetitive injection attempts
    /(system|assistant|user).*\1.*\1/gi, // Repeated role mentions
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(input)) {
      logSecurityEvent(SecurityEventType.PROMPT_INJECTION_BLOCKED, { reason: 'suspicious_pattern' });
      return { isValid: false, sanitized: '', reason: SECURITY_CONFIG.ERROR_MESSAGES.INVALID_INPUT };
    }
  }

  const sanitized = sanitizeText(input);
  
  // Final check: ensure sanitization didn't leave dangerous content
  if (sanitized.includes('[FILTERED]') && sanitized.split('[FILTERED]').length > 3) {
    logSecurityEvent(SecurityEventType.PROMPT_INJECTION_BLOCKED, { reason: 'excessive_filtering' });
    return { isValid: false, sanitized: '', reason: SECURITY_CONFIG.ERROR_MESSAGES.INVALID_INPUT };
  }
  
  return { isValid: true, sanitized };
};

/**
 * Sanitizes data for safe logging to prevent log injection
 * @param data - The data to log
 * @returns Sanitized data safe for logging
 */
export const sanitizeForLogging = (data: any): string => {
  if (data === null || data === undefined) {
    return 'null';
  }
  
  const str = typeof data === 'string' ? data : JSON.stringify(data);
  
  let sanitized = str
    .replace(/[\r\n]/g, ' ')  // Remove line breaks
    .replace(/[\x00-\x1F\x7F]/g, '')  // Remove control characters
    .substring(0, 200);  // Limit length
    
  // Redact sensitive information if enabled
  if (SECURITY_CONFIG.REDACT_SENSITIVE_DATA) {
    sanitized = sanitized.replace(/\b(password|token|key|secret|api[_-]?key)\b/gi, '[REDACTED]');
  }
  
  return sanitized;
};